import destr from "destr";
import { onError } from "@apollo/client/link/error";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import { ApolloClients, provideApolloClients } from "@vue/apollo-composable";
import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache } from "@apollo/client/core";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { setContext } from "@apollo/client/link/context";
import { sha256 } from "ohash";
import { defineNuxtPlugin, ref, useCookie, useRequestHeaders, useRuntimeConfig } from "#imports";
export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();
  const apolloConfig = config.public.apollo;
  if (!apolloConfig) {
    return;
  }
  const requestCookies = process.server && apolloConfig.proxyCookies && useRequestHeaders(["cookie"]) || void 0;
  const clients = {};
  for (const [key, clientConfig] of Object.entries(apolloConfig.clients)) {
    const getAuth = async () => {
      const token = ref("");
      await nuxtApp.callHook("apollo:auth", {
        token,
        client: key
      });
      if (!token.value) {
        if (clientConfig.tokenStorage === "cookie") {
          if (process.client) {
            token.value = useCookie(clientConfig.tokenName).value ?? "";
          } else if (requestCookies?.cookie) {
            token.value = requestCookies.cookie.split(";").find((c) => c.trim().startsWith(`${clientConfig.tokenName}=`))?.split("=")?.[1] ?? "";
          }
        } else if (process.client && clientConfig.tokenStorage === "localStorage") {
          token.value = localStorage.getItem(clientConfig.tokenName) ?? "";
        }
        if (!token.value) {
          return;
        }
      }
      const authScheme = !!token.value?.match(/^[a-zA-Z]+\s/)?.[0];
      if (authScheme || clientConfig?.authType === null) {
        return token.value;
      }
      return `${clientConfig?.authType} ${token.value}`;
    };
    const authLink = setContext(async (_, { headers }) => {
      const auth = await getAuth();
      if (!auth) {
        return;
      }
      return {
        headers: {
          ...headers,
          ...requestCookies && requestCookies,
          [clientConfig.authHeader]: auth
        }
      };
    });
    const headerLink = setContext(async (_, { headers }) => {
      headers = ref(headers ?? {});
      await nuxtApp.callHook("apollo:headers", headers);
      return {
        headers: {
          ...headers.value
        }
      };
    });
    const httpLinkOptions = {
      ...clientConfig?.httpLinkOptions && clientConfig.httpLinkOptions,
      uri: process.client && clientConfig.browserHttpEndpoint || clientConfig.httpEndpoint,
      headers: { ...clientConfig?.httpLinkOptions?.headers || {} }
    };
    let httpLink = authLink.concat(headerLink).concat(clientConfig.useUploadLink ? createUploadLink(httpLinkOptions) : createHttpLink(httpLinkOptions));
    if (clientConfig.useAutomaticPersistedQueries) {
      httpLink = createPersistedQueryLink({
        sha256,
        useGETForHashedQueries: true
      }).concat(httpLink);
    }
    const errorLink = onError((err) => {
      nuxtApp.callHook("apollo:error", err);
    });
    const link = ApolloLink.from([
      errorLink,
      httpLink
    ]);
    const cache = new InMemoryCache(clientConfig.inMemoryCacheOptions);
    clients[key] = new ApolloClient({
      link,
      cache,
      ...apolloConfig.clientAwareness && { name: key },
      ...process.server ? { ssrMode: true } : { ssrForceFetchDelay: 100 },
      connectToDevTools: clientConfig.connectToDevTools || false,
      defaultOptions: clientConfig?.defaultOptions
    });
    if (!clients?.default && !apolloConfig?.clients?.default && key === Object.keys(apolloConfig.clients)[0]) {
      clients.default = clients[key];
    }
    const cacheKey = `_apollo:${key}`;
    nuxtApp.hook("app:rendered", () => {
      nuxtApp.payload.data[cacheKey] = cache.extract();
    });
    if (process.client && nuxtApp.payload.data[cacheKey]) {
      cache.restore(destr(JSON.stringify(nuxtApp.payload.data[cacheKey])));
    }
  }
  provideApolloClients(clients);
  nuxtApp.vueApp.provide(ApolloClients, clients);
  nuxtApp._apolloClients = clients;
  const defaultClient = clients?.default;
  return {
    provide: {
      apollo: {
        clients,
        defaultClient
      }
    }
  };
});
